import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Chip, CircularProgress, Collapse, Checkbox,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton, IconButton, Tooltip, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Groups as TeamIcon, PersonAddAlt1 as InviteIcon, Close as CloseIcon,
  ContentCopy as CopyIcon, Link as LinkIcon, DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon, HourglassEmpty as PendingIcon, CheckCircle as CheckIcon,
} from '@mui/icons-material';
import axios, { API } from '../api';
import { designTokens as t } from '../theme';

// Pages autorisables (mêmes clés que la sidebar / le backend).
const PAGES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'create', label: 'Créer un post' },
  { key: 'scheduled', label: 'Scheduled Posts' },
  { key: 'automation', label: 'Automatisation' },
  { key: 'accounts', label: 'Comptes' },
  { key: 'library', label: 'Post Library' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'proxies', label: 'Proxies' },
  { key: 'profiles', label: 'Profils mobiles' },
  { key: 'groups', label: 'Groupes' },
];

const EMPTY_PERMS = { pages: ['dashboard'], all_accounts: true, accounts: [] };

const label = (key) => (PAGES.find((p) => p.key === key) || { label: key }).label;

// Résumé compact des permissions d'un membre (pour les lignes de liste).
function permsSummary(perms) {
  const pages = (perms?.pages || []).length;
  const acc = perms?.all_accounts ? 'tous les comptes' : `${(perms?.accounts || []).length} compte(s)`;
  return `${pages} page(s) · ${acc}`;
}

// ── Éditeur de permissions (partagé : invitation + édition d'un membre) ─────────
function PermissionEditor({ value, onChange, accounts }) {
  const togglePage = (key) => {
    const pages = value.pages.includes(key)
      ? value.pages.filter((p) => p !== key)
      : [...value.pages, key];
    onChange({ ...value, pages });
  };
  const toggleAccount = (name) => {
    const list = value.accounts.includes(name)
      ? value.accounts.filter((a) => a !== name)
      : [...value.accounts, name];
    onChange({ ...value, accounts: list });
  };
  return (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1 }}>
        Permissions
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        {PAGES.map((p) => {
          const on = value.pages.includes(p.key);
          return (
            <Box
              key={p.key}
              onClick={() => togglePage(p.key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6,
                borderRadius: 2, cursor: 'pointer', userSelect: 'none',
                border: `1px solid ${on ? alpha(t.primary, 0.5) : t.border}`,
                bgcolor: on ? alpha(t.primary, 0.1) : alpha('#fff', 0.015),
                transition: 'all .15s ease',
                '&:hover': { borderColor: alpha(t.primary, 0.4) },
              }}
            >
              <Checkbox checked={on} size="small" sx={{ p: 0.5 }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: on ? 600 : 500, color: on ? t.foreground : t.muted }}>
                {p.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mt: 2.5, mb: 1 }}>
        Accès aux comptes
      </Typography>
      <ToggleButtonGroup
        exclusive size="small"
        value={value.all_accounts ? 'all' : 'restricted'}
        onChange={(e, v) => { if (v) onChange({ ...value, all_accounts: v === 'all' }); }}
      >
        <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>Tous les comptes</ToggleButton>
        <ToggleButton value="restricted" sx={{ textTransform: 'none', px: 1.5 }}>Accès restreint</ToggleButton>
      </ToggleButtonGroup>

      <Collapse in={!value.all_accounts} unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {accounts.length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>Aucun compte Threads ajouté pour l'instant.</Typography>
          )}
          {accounts.map((name) => {
            const on = value.accounts.includes(name);
            return (
              <Chip
                key={name} label={`@${name}`} size="small" onClick={() => toggleAccount(name)}
                sx={{
                  fontWeight: 600, cursor: 'pointer',
                  bgcolor: on ? alpha(t.primary, 0.16) : alpha('#fff', 0.03),
                  color: on ? '#c7d2fe' : t.muted,
                  border: `1px solid ${on ? alpha(t.primary, 0.4) : t.border}`,
                }}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function Team() {
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState({ owner_email: '', limit: 5, members: [], invites: [] });
  const [accounts, setAccounts] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState(EMPTY_PERMS);
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [edit, setEdit] = useState(null); // { id, email, perms }
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // member
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const load = useCallback(async () => {
    try {
      const [{ data: tm }, { data: sess }] = await Promise.all([
        axios.get(`${API}/api/team`),
        axios.get(`${API}/api/sessions`),
      ]);
      setTeam(tm);
      setAccounts((Array.isArray(sess) ? sess : []).map((s) => s.Name || s.name).filter(Boolean));
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notify('Lien copié dans le presse-papier');
    } catch {
      notify('Impossible de copier — copie le manuellement', 'error');
    }
  };

  const createInvite = async () => {
    if (!email.trim()) { notify('Renseigne un email', 'error'); return; }
    if (perms.pages.length === 0) { notify('Coche au moins une page', 'error'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/team/invites`, { email: email.trim(), permissions: perms });
      setCreatedLink(inviteLink(data.token));
      setEmail('');
      await load();
      notify('Invitation créée — copie le lien et transmets-le');
    } catch (e) {
      notify(e.response?.data?.error || "Erreur lors de la création de l'invitation", 'error');
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (token) => {
    try {
      await axios.delete(`${API}/api/team/invites/${token}`);
      await load();
      notify('Invitation révoquée');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur', 'error');
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/api/team/members/${edit.id}`, { permissions: edit.perms });
      setEdit(null);
      await load();
      notify('Permissions mises à jour');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const removeMember = async () => {
    try {
      await axios.delete(`${API}/api/team/members/${confirmDelete.id}`);
      setConfirmDelete(null);
      await load();
      notify('Membre retiré');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur', 'error');
    }
  };

  const ownerName = (team.owner_email || '').split('@')[0] || 'Mon';
  const memberCount = team.members.length;
  const totalCount = memberCount + team.invites.length;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  const card = { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', color: t.mutedFaint, textTransform: 'uppercase' }}>
        Settings
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, mt: 0.25 }}>Team</Typography>

      {/* Carte équipe */}
      <Box sx={{ ...card, display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75, mb: 2 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(t.primary, 0.16), color: t.primary }}>
          <TeamIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, textTransform: 'capitalize' }}>
            Équipe de {ownerName}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>
            {memberCount} / {team.limit} membres{team.invites.length > 0 ? ` · ${team.invites.length} invitation(s) en attente` : ''}
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<InviteIcon sx={{ fontSize: 18 }} />}
          onClick={() => { setInviteOpen((o) => !o); setCreatedLink(''); }}
          disabled={totalCount >= team.limit}
          sx={{ borderRadius: 5, px: 2.25, textTransform: 'none', fontWeight: 700 }}
        >
          Inviter
        </Button>
      </Box>

      {/* Panneau d'invitation */}
      <Collapse in={inviteOpen} unmountOnExit>
        <Box sx={{ ...card, p: 2.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Inviter un membre</Typography>
            <IconButton size="small" onClick={() => setInviteOpen(false)} sx={{ color: t.mutedFaint }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1 }}>
            Adresse email
          </Typography>
          <TextField
            fullWidth size="small" type="email" placeholder="membre@exemple.com"
            value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2.5 }}
          />

          <PermissionEditor value={perms} onChange={setPerms} accounts={accounts} />

          <Box sx={{ mt: 2.5 }}>
            {!createdLink ? (
              <Button
                fullWidth variant="contained" disabled={creating}
                startIcon={creating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <LinkIcon sx={{ fontSize: 18 }} />}
                onClick={createInvite}
                sx={{ py: 1.1, textTransform: 'none', fontWeight: 700 }}
              >
                Créer le lien d'invitation
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.25, borderRadius: 2, bgcolor: alpha('#22c55e', 0.08), border: `1px solid ${alpha('#22c55e', 0.3)}` }}>
                <CheckIcon sx={{ fontSize: 18, color: '#86efac', flexShrink: 0 }} />
                <Typography noWrap sx={{ flex: 1, fontSize: '0.82rem', fontFamily: 'monospace', color: '#d4d4d8' }}>
                  {createdLink}
                </Typography>
                <Button size="small" variant="outlined" startIcon={<CopyIcon sx={{ fontSize: 15 }} />} onClick={() => copy(createdLink)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                  Copier le lien
                </Button>
              </Box>
            )}
            <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint, mt: 1 }}>
              Aucun email n'est envoyé : transmets le lien à la personne, elle définira son mot de passe et rejoindra directement l'équipe avec ces permissions.
            </Typography>
          </Box>
        </Box>
      </Collapse>

      {/* Membres */}
      <Box sx={{ ...card, mb: 2 }}>
        <Typography sx={{ px: 2.25, pt: 1.75, pb: 0.75, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase' }}>
          Membres ({memberCount})
        </Typography>
        {memberCount === 0 && (
          <Typography sx={{ px: 2.25, pb: 2, fontSize: '0.85rem', color: t.mutedFaint }}>
            Aucun membre pour l'instant — invite quelqu'un avec le bouton ci-dessus.
          </Typography>
        )}
        {team.members.map((m, i) => (
          <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.4, borderTop: i === 0 ? 'none' : `1px solid ${alpha(t.border, 0.7)}` }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: `linear-gradient(135deg, ${t.primary}, ${t.primaryHover})`, color: '#fff', fontSize: '0.82rem', fontWeight: 700 }}>
              {(m.email || '?').charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{m.email}</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{permsSummary(m.permissions)}</Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 380 }}>
              {(m.permissions?.pages || []).slice(0, 4).map((p) => (
                <Chip key={p} label={label(p)} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: alpha(t.primary, 0.12), color: '#c7d2fe' }} />
              ))}
              {(m.permissions?.pages || []).length > 4 && (
                <Chip label={`+${(m.permissions.pages || []).length - 4}`} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: alpha('#fff', 0.05), color: t.muted }} />
              )}
            </Box>
            <Tooltip title="Modifier les permissions" arrow>
              <IconButton size="small" onClick={() => setEdit({ id: m.id, email: m.email, perms: { pages: m.permissions?.pages || [], all_accounts: m.permissions?.all_accounts !== false, accounts: m.permissions?.accounts || [] } })} sx={{ color: t.muted }}>
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Retirer de l'équipe" arrow>
              <IconButton size="small" onClick={() => setConfirmDelete(m)} sx={{ color: t.muted, '&:hover': { color: '#fca5a5' } }}>
                <DeleteIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {/* Invitations en attente */}
      {team.invites.length > 0 && (
        <Box sx={card}>
          <Typography sx={{ px: 2.25, pt: 1.75, pb: 0.75, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase' }}>
            Invitations en attente ({team.invites.length})
          </Typography>
          {team.invites.map((inv, i) => (
            <Box key={inv.token} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.4, borderTop: i === 0 ? 'none' : `1px solid ${alpha(t.border, 0.7)}` }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: alpha('#eab308', 0.14), color: '#fde047' }}>
                <PendingIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{inv.email}</Typography>
                <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{permsSummary(inv.permissions)} · en attente d'acceptation</Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<CopyIcon sx={{ fontSize: 15 }} />} onClick={() => copy(inviteLink(inv.token))} sx={{ textTransform: 'none' }}>
                Copier le lien
              </Button>
              <Tooltip title="Révoquer l'invitation" arrow>
                <IconButton size="small" onClick={() => revokeInvite(inv.token)} sx={{ color: t.muted, '&:hover': { color: '#fca5a5' } }}>
                  <DeleteIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Dialog : modifier un membre */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Permissions — {edit?.email}</DialogTitle>
        <DialogContent>
          {edit && <PermissionEditor value={edit.perms} onChange={(p) => setEdit({ ...edit, perms: p })} accounts={accounts} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEdit(null)} sx={{ textTransform: 'none' }}>Annuler</Button>
          <Button variant="contained" onClick={saveEdit} disabled={savingEdit} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {savingEdit ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog : confirmer le retrait d'un membre */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Retirer ce membre ?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: t.muted }}>
            <b>{confirmDelete?.email}</b> perdra immédiatement l'accès à l'équipe. Son compte de connexion sera supprimé.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>Annuler</Button>
          <Button variant="contained" color="error" onClick={removeMember} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Retirer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
